/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useContext, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import CustomButton from "../../../components/common/Button/CustomButton";
import AddIcon from "@mui/icons-material/Add";
import ProductsAPI from "../../../api/firebase/ProductsAPI";
import {
  Avatar,
  Snackbar,
  Typography,
  Paper,
  Stack,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Badge,
  Drawer,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import AddCategory from "../../../components/admin/add-category/AddCategory";
import AllCategoriesContext from "../../../context/allCategories/AllCategoriesContext";
import AllProductsContext from "../../../context/allProducts/AllProductsContext";
import { getProductsUsingCategory } from "../../../utils/categoryProducts";
import { styles } from "./styles";
import CustomDataGrid from "../../../components/common/DataGrid/CustomDataGrid";
import AdminStatCard from "../../../components/admin/stat-card/AdminStatCard";
import AddCategoryContext from "../../../context/addCategory/AddCategoryContext";
import { formatDateTimeForAdmin } from "../../../utils/dateUtils";

// Enhanced Icons
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearIcon from "@mui/icons-material/Clear";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneIcon from "@mui/icons-material/Tune";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const CategoriesListAdmin = () => {
  const initState = {
    id: "",
    name: "",
    parentCategory: {
      id: "None",
      name: "None",
    },
    images: [],
    showInNavbar: true,
  };

  const productAPI = useMemo(() => new ProductsAPI(), []);
  const allCategoriesContext = useContext(AllCategoriesContext);
  const allProductsContext = useContext(AllProductsContext);
  const categoryContext = useContext(AddCategoryContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchQuery, setSearchQuery] = useState("");
  const [hierarchyFilter, setHierarchyFilter] = useState("ALL"); // ALL, ROOT, SUB
  const [navbarFilter, setNavbarFilter] = useState("ALL"); // ALL, VISIBLE, HIDDEN
  const [parentFilter, setParentFilter] = useState("ALL");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // Delete confirmation dialog state
  const [deleteDialogCategory, setDeleteDialogCategory] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Add / Edit Modal state
  const [openCategoryModal, setOpenCategoryModal] = useState(false);
  const [editCategoryModal, setEditCategoryModal] = useState(false);

  // Snackbar notifications
  const [snackbarState, setSnackbarState] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "success" });

  const showSnackbar = (text, type = "success") => {
    setMessage({ text, type });
    setSnackbarState(true);
  };

  const hideSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarState(false);
  };

  const categories = Array.isArray(allCategoriesContext.state)
    ? allCategoriesContext.state
    : [];

  const products = Array.isArray(allProductsContext?.state)
    ? allProductsContext.state
    : [];

  // Metrics
  const visibleInNavCount = categories.filter((c) => c.showInNavbar !== false).length;
  const rootCategoriesCount = categories.filter(
    (c) =>
      !c.parentCategory ||
      c.parentCategory.id === "None" ||
      c.parentCategory.name === "None"
  ).length;
  const subCategoriesCount = categories.length - rootCategoriesCount;

  // Root categories list for parent filter dropdown
  const rootCategories = useMemo(() => {
    return categories.filter(
      (c) =>
        !c.parentCategory ||
        c.parentCategory.id === "None" ||
        c.parentCategory.name === "None"
    );
  }, [categories]);

  // Filtered categories
  const filteredCategories = useMemo(() => {
    return categories.filter((category) => {
      // 1. Text Search
      if (searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase();
        const matches = [
          category.name,
          category.id,
          category.parentCategory?.name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);

        if (!matches) return false;
      }

      // 2. Hierarchy Filter
      const isRoot =
        !category.parentCategory ||
        category.parentCategory.id === "None" ||
        category.parentCategory.name === "None";

      if (hierarchyFilter === "ROOT" && !isRoot) return false;
      if (hierarchyFilter === "SUB" && isRoot) return false;

      // 3. Navbar Visibility Filter
      const isVisible = category.showInNavbar !== false;
      if (navbarFilter === "VISIBLE" && !isVisible) return false;
      if (navbarFilter === "HIDDEN" && isVisible) return false;

      // 4. Parent Category Filter
      if (parentFilter !== "ALL") {
        if (
          category.parentCategory?.id !== parentFilter &&
          category.parentCategory?.name !== parentFilter
        ) {
          return false;
        }
      }

      return true;
    });
  }, [categories, searchQuery, hierarchyFilter, navbarFilter, parentFilter]);

  // Fetch all categories and products
  const fetchData = async () => {
    setIsRefreshing(true);
    try {
      const resCategories = await productAPI.getCategories();
      if (resCategories && resCategories.data) {
        allCategoriesContext.setState(resCategories.data);
      }
      const resProducts = await productAPI.getProducts();
      if (resProducts && resProducts.data && allProductsContext?.setState) {
        allProductsContext.setState(resProducts.data);
      }
    } catch (err) {
      console.error("Error fetching categories or products:", err);
      showSnackbar("Failed to refresh categories data.", "error");
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const showAddCategoryModal = (action) => {
    if (action === "ADD") {
      setEditCategoryModal(false);
      categoryContext.setState(initState);
    }
    setOpenCategoryModal(true);
  };

  const hideAddCategoryModal = () => {
    setOpenCategoryModal(false);
  };

  const setEditCategory = (category) => {
    setEditCategoryModal(true);
    categoryContext.setState(category);
    setOpenCategoryModal(true);
  };

  // Toggle Navbar Visibility directly
  const handleToggleNavbarVisibility = async (category) => {
    const currentVal = category.showInNavbar !== false;
    const newVal = !currentVal;
    const updatedCategory = {
      ...category,
      showInNavbar: newVal,
    };

    try {
      const res = await productAPI.setCategory({
        id: category.id,
        payload: updatedCategory,
      });

      if (res && res.success) {
        showSnackbar(
          `Category "${category.name}" is now ${
            newVal ? "visible in navigation" : "hidden from navigation"
          }.`,
          "success"
        );
        const updated = allCategoriesContext.state.map((c) =>
          c.id === category.id ? { ...c, showInNavbar: newVal } : c
        );
        allCategoriesContext.setState(updated);
      } else {
        showSnackbar(res?.message || "Failed to update navbar status.", "error");
      }
    } catch (err) {
      showSnackbar("Error updating navbar visibility.", "error");
    }
  };


  const handleExecuteDelete = async (categoryToDelete = null) => {
    const target = categoryToDelete || deleteDialogCategory;
    if (!target) return;

    // Check if any product uses this category
    let currentProducts = products;
    if (!currentProducts || currentProducts.length === 0) {
      try {
        const res = await productAPI.getProducts();
        if (res && res.data) {
          currentProducts = res.data;
          if (allProductsContext?.setState) {
            allProductsContext.setState(res.data);
          }
        }
      } catch (err) {
        console.error("Failed to verify products before category deletion:", err);
      }
    }

    const linkedProducts = getProductsUsingCategory(currentProducts, target);
    if (linkedProducts.length > 0) {
      showSnackbar(
        `Cant delete category. ${linkedProducts.length} product${
          linkedProducts.length > 1 ? "s are" : " is"
        } currently using this category.`,
        "error"
      );
      setDeleteDialogCategory(null);
      return;
    }

    setIsDeleting(true);
    try {
      const res = await productAPI.deleteCategory(target.id);
      if (res && res.success) {
        showSnackbar(
          `Category "${target.name}" deleted successfully.`,
          "success"
        );
        const updated = allCategoriesContext.state.filter(
          (c) => c.id !== target.id
        );
        allCategoriesContext.setState(updated);
        setDeleteDialogCategory(null);
      } else {
        showSnackbar(res?.message || "Error deleting category.", "error");
      }
    } catch (err) {
      showSnackbar("An error occurred while deleting the category.", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  // Save/Update Category from AddCategory modal
  const handleSetCategory = (
    action = "ADD",
    category_id = null,
    data = categoryContext.state
  ) => {
    if (category_id === null) {
      category_id = data.name.trim().toUpperCase();
      category_id = category_id.replace(/ /g, "_");
    }

    const res = productAPI.setCategory({
      id: category_id,
      payload: data,
    });

    res
      .then((resData) => {
        if (resData.success) {
          const action_msg = action === "ADD" ? "added" : "updated";
          showSnackbar(`Category ${action_msg} successfully!`, "success");

          if (action === "ADD") {
            const newCat = {
              ...data,
              id: category_id,
              createdAt: { seconds: Date.now() / 1000 },
            };
            categoryContext.setState(newCat);
            allCategoriesContext.setState([newCat, ...allCategoriesContext.state]);
          } else {
            const categoryIndex = allCategoriesContext.state.findIndex(
              (obj) => obj.id === category_id
            );
            const categories_ = [...allCategoriesContext.state];
            if (categoryIndex !== -1) {
              categories_[categoryIndex] = {
                ...categories_[categoryIndex],
                ...data,
              };
              allCategoriesContext.setState(categories_);
            }
          }
          categoryContext.setState(initState);
          hideAddCategoryModal();
        } else {
          showSnackbar(resData.message || "An error occurred. Please try again.", "error");
        }
      })
      .catch((ex) => {
        showSnackbar("An error occurred. Please try again.", "error");
        console.error(ex);
      });
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchQuery("");
    setHierarchyFilter("ALL");
    setNavbarFilter("ALL");
    setParentFilter("ALL");
  };

  const hasActiveFilters =
    searchQuery.trim() !== "" ||
    hierarchyFilter !== "ALL" ||
    navbarFilter !== "ALL" ||
    parentFilter !== "ALL";

  const activeFiltersCount =
    (searchQuery.trim() !== "" ? 1 : 0) +
    (hierarchyFilter !== "ALL" ? 1 : 0) +
    (navbarFilter !== "ALL" ? 1 : 0) +
    (parentFilter !== "ALL" ? 1 : 0);

  // Check linked child categories for delete dialog warning
  const deleteDialogChildCount = deleteDialogCategory
    ? categories.filter(
        (c) =>
          c.parentCategory?.id === deleteDialogCategory.id ||
          c.parentCategory?.name === deleteDialogCategory.name
      ).length
    : 0;

  const deleteDialogLinkedProducts = useMemo(() => {
    if (!deleteDialogCategory) return [];
    return getProductsUsingCategory(products, deleteDialogCategory);
  }, [deleteDialogCategory, products]);

  // Map categoryId to product count for fast lookup and column sorting
  const categoryProductCountMap = useMemo(() => {
    const map = {};
    categories.forEach((category) => {
      map[category.id] = getProductsUsingCategory(products, category).length;
    });
    return map;
  }, [categories, products]);

  // DataGrid Columns definition
  const columns = [
    {
      field: "images",
      headerName: "Photo",
      width: 75,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const imgs = params.row.images || [];
        const hasImg = imgs.length > 0 && imgs[0]?.url;
        return (
          <Avatar
            variant="rounded"
            src={hasImg ? imgs[0].url : ""}
            sx={{
              width: 38,
              height: 38,
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              bgcolor: "#f8fafc",
            }}
          >
            <FolderOutlinedIcon sx={{ fontSize: "1.2rem", color: "#94a3b8" }} />
          </Avatar>
        );
      },
    },
    {
      field: "id",
      headerName: "ID",
      hide: true,
    },
    {
      field: "name",
      headerName: "Category Name",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, overflow: "hidden" }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: "#1e293b",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.row.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontFamily: "ui-monospace, monospace",
              fontSize: "0.7rem",
              fontWeight: 600,
              color: "#64748b",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            #{params.row.id}
          </Typography>
        </Box>
      ),
    },
    {
      field: "parentCategory",
      headerName: "Hierarchy / Parent",
      width: 220,
      renderCell: (params) => {
        const parentName = params.row.parentCategory?.name;
        const isRoot = !parentName || parentName === "None";
        if (isRoot) {
          return (
            <Chip
              label="Root Category"
              size="small"
              sx={{
                height: 24,
                fontSize: "0.72rem",
                fontWeight: 700,
                bgcolor: "#f1f5f9",
                color: "#475569",
                border: "1px solid #e2e8f0",
              }}
            />
          );
        }
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <Typography variant="body2" sx={{ color: "#94a3b8", fontSize: "0.85rem" }}>
              ↳
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155" }}>
              {parentName}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "assignedProducts",
      headerName: "Assigned Products",
      width: 175,
      type: "number",
      align: "left",
      headerAlign: "left",
      valueGetter: (params) => categoryProductCountMap[params.row.id] ?? 0,
      renderCell: (params) => {
        const count = params.value ?? (categoryProductCountMap[params.row.id] || 0);
        return (
          <Chip
            icon={<Inventory2OutlinedIcon sx={{ fontSize: "14px !important" }} />}
            label={count === 1 ? "1 product" : `${count} products`}
            size="small"
            sx={{
              height: 24,
              fontSize: "0.75rem",
              fontWeight: 700,
              bgcolor: count > 0 ? "rgba(0, 77, 64, 0.08)" : "#f1f5f9",
              color: count > 0 ? "#004d40" : "#64748b",
              border: `1px solid ${count > 0 ? "rgba(0, 77, 64, 0.2)" : "#e2e8f0"}`,
              "& .MuiChip-icon": {
                color: count > 0 ? "#004d40" : "#94a3b8",
              },
            }}
          />
        );
      },
    },
    {
      field: "showInNavbar",
      headerName: "Navbar Visibility",
      width: 170,
      renderCell: (params) => {
        const isVisible = params.row.showInNavbar !== false;
        return (
          <Chip
            label={isVisible ? "Visible in Nav" : "Hidden"}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleNavbarVisibility(params.row);
            }}
            sx={{
              height: 24,
              fontSize: "0.75rem",
              fontWeight: 700,
              bgcolor: isVisible ? "#ecfdf5" : "#f1f5f9",
              color: isVisible ? "#047857" : "#64748b",
              border: `1px solid ${isVisible ? "#a7f3d0" : "#cbd5e1"}`,
              cursor: "pointer",
              transition: "all 0.15s ease",
              "&:hover": {
                bgcolor: isVisible ? "#d1fae5" : "#e2e8f0",
                transform: "translateY(-1px)",
              },
            }}
          />
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Created at",
      width: 180,
      renderCell: (params) => {
        const t = formatDateTimeForAdmin(params.row.createdAt);
        return (
          <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
            {t}
          </Typography>
        );
      },
    },
  ];

  return (
    <Box sx={{ pb: 4 }}>
      {/* Hero Header */}
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: 4,
          color: "#ffffff",
          background: "linear-gradient(135deg, #00382e 0%, #004d40 60%, #056152 100%)",
          boxShadow: "0 10px 25px -5px rgba(0, 77, 64, 0.3)",
          display: { xs: "none", sm: "flex" },
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: 2.5,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box sx={{ zIndex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Box
              sx={{
                p: 0.5,
                borderRadius: "6px",
                bgcolor: "rgba(255,255,255,0.15)",
                display: "inline-flex",
              }}
            >
              <CategoryOutlinedIcon fontSize="small" />
            </Box>
            <Typography
              variant="overline"
              sx={{
                fontWeight: 800,
                letterSpacing: "0.1em",
                color: "#a7f3d0",
                fontSize: "0.75rem",
              }}
            >
              TAXONOMY & NAVIGATION HIERARCHY
            </Typography>
          </Stack>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.75rem", sm: "2.2rem" },
              letterSpacing: "-0.02em",
            }}
          >
            Categories
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.88, mt: 0.5, maxWidth: 520 }}>
            Organize catalog hierarchy, storefront navbar visibility, subcategories, and category banners in real-time.
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ zIndex: 1, alignSelf: { xs: "stretch", sm: "auto" } }}
        >
          <Tooltip title="Refresh Categories Data">
            <IconButton
              onClick={fetchData}
              disabled={isRefreshing}
              sx={{
                bgcolor: "rgba(255,255,255,0.15)",
                color: "#ffffff",
                "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
              }}
            >
              <RefreshIcon
                sx={{
                  animation: isRefreshing ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
            </IconButton>
          </Tooltip>

          <CustomButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => showAddCategoryModal("ADD")}
            sx={{
              fontWeight: 800,
              bgcolor: "#ffffff",
              color: "#004d40",
              px: 3,
              py: 1.2,
              borderRadius: "10px",
              boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
              "&:hover": { bgcolor: "#f8fafc", color: "#00382e" },
            }}
          >
            Add New Category
          </CustomButton>
        </Stack>
      </Box>

      {/* KPI Summary Cards */}
      <Grid2 container spacing={{ xs: 1.25, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        {/* Total Categories */}
        <Grid2 xs={6} sm={6} md={3}>
          <AdminStatCard
            label="Total Categories"
            value={categories.length}
            note={`${rootCategoriesCount} root • ${subCategoriesCount} subcategories`}
            icon={<CategoryOutlinedIcon />}
            onClick={() => {
              setHierarchyFilter("ALL");
              setNavbarFilter("ALL");
              setParentFilter("ALL");
            }}
          />
        </Grid2>

        {/* Live In Navbar */}
        <Grid2 xs={6} sm={6} md={3}>
          <AdminStatCard
            label="In Navbar Menu"
            value={visibleInNavCount}
            note="Visible in storefront navigation"
            icon={<CheckCircleOutlineRoundedIcon />}
            color="#15803d"
            background="#f0fdf4"
            selected={navbarFilter === "VISIBLE"}
            onClick={() => setNavbarFilter(navbarFilter === "VISIBLE" ? "ALL" : "VISIBLE")}
            delay={70}
          />
        </Grid2>

        {/* Root Categories */}
        <Grid2 xs={6} sm={6} md={3}>
          <AdminStatCard
            label="Root Categories"
            value={rootCategoriesCount}
            note="Top-level primary taxonomy groups"
            icon={<AccountTreeOutlinedIcon />}
            color="#0369a1"
            background="#f0f9ff"
            selected={hierarchyFilter === "ROOT"}
            onClick={() => setHierarchyFilter(hierarchyFilter === "ROOT" ? "ALL" : "ROOT")}
            delay={140}
          />
        </Grid2>

        {/* Subcategories */}
        <Grid2 xs={6} sm={6} md={3}>
          <AdminStatCard
            label="Subcategories"
            value={subCategoriesCount}
            note="Nested sub-level categories"
            icon={<LayersOutlinedIcon />}
            color="#b45309"
            background="#fffbeb"
            selected={hierarchyFilter === "SUB"}
            onClick={() => setHierarchyFilter(hierarchyFilter === "SUB" ? "ALL" : "SUB")}
            delay={210}
          />
        </Grid2>
      </Grid2>

      {!isMobile && (
        <>
          {/* Desktop Filter and Search Bar */}
          <Paper elevation={0} sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 3, mb: 2 }}>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "center" }}
                justifyContent="space-between"
              >
                <TextField
                  size="small"
                  placeholder="Search by category name, ID, or parent..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchQuery("")}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiOutlinedInput-root": { bgcolor: "#f8fafc", borderRadius: 2 },
                  }}
                />

                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="hierarchy-filter-label">Hierarchy</InputLabel>
                    <Select
                      labelId="hierarchy-filter-label"
                      value={hierarchyFilter}
                      label="Hierarchy"
                      onChange={(e) => setHierarchyFilter(e.target.value)}
                      sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}
                    >
                      <MenuItem value="ALL">All Hierarchy</MenuItem>
                      <MenuItem value="ROOT">Root Categories</MenuItem>
                      <MenuItem value="SUB">Subcategories</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="navbar-filter-label">Navbar Visibility</InputLabel>
                    <Select
                      labelId="navbar-filter-label"
                      value={navbarFilter}
                      label="Navbar Visibility"
                      onChange={(e) => setNavbarFilter(e.target.value)}
                      sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}
                    >
                      <MenuItem value="ALL">All Statuses</MenuItem>
                      <MenuItem value="VISIBLE">Visible in Nav</MenuItem>
                      <MenuItem value="HIDDEN">Hidden from Nav</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="parent-filter-label">Parent Group</InputLabel>
                    <Select
                      labelId="parent-filter-label"
                      value={parentFilter}
                      label="Parent Group"
                      onChange={(e) => setParentFilter(e.target.value)}
                      sx={{ borderRadius: 2, bgcolor: "#f8fafc" }}
                    >
                      <MenuItem value="ALL">All Parents</MenuItem>
                      {rootCategories.map((cat) => (
                        <MenuItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ pt: 1, borderTop: "1px dashed #f1f5f9" }}
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                    Showing {filteredCategories.length} of {categories.length} categories
                  </Typography>

                  {hasActiveFilters && (
                    <Button
                      size="small"
                      onClick={handleResetFilters}
                      sx={{
                        fontSize: "0.75rem",
                        textTransform: "none",
                        color: "#ef4444",
                        p: 0,
                        minWidth: "auto",
                        fontWeight: 700,
                      }}
                    >
                      Clear all filters
                    </Button>
                  )}

                  {searchQuery.trim() && (
                    <Chip
                      size="small"
                      label={`Search: "${searchQuery}"`}
                      onDelete={() => setSearchQuery("")}
                    />
                  )}
                  {hierarchyFilter !== "ALL" && (
                    <Chip
                      size="small"
                      label={`Hierarchy: ${
                        hierarchyFilter === "ROOT" ? "Root Categories" : "Subcategories"
                      }`}
                      onDelete={() => setHierarchyFilter("ALL")}
                    />
                  )}
                  {navbarFilter !== "ALL" && (
                    <Chip
                      size="small"
                      label={`Navbar: ${navbarFilter === "VISIBLE" ? "Visible" : "Hidden"}`}
                      onDelete={() => setNavbarFilter("ALL")}
                    />
                  )}
                  {parentFilter !== "ALL" && (
                    <Chip
                      size="small"
                      label={`Parent: ${
                        rootCategories.find((c) => c.id === parentFilter)?.name || parentFilter
                      }`}
                      onDelete={() => setParentFilter("ALL")}
                    />
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Paper>

          <CustomDataGrid
            rows={filteredCategories}
            columns={columns}
            styles={{ height: "620px" }}
            initialState={{
              sorting: {
                sortModel: [{ field: "name", sort: "asc" }],
              },
            }}
            pageSizes={[50, 100]}
            onRowClickHandle={(params) => setEditCategory(params.row)}
          />
        </>
      )}

      {isMobile && (
        <Box sx={{ pb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b", mb: 1.25 }}>
              Categories ({filteredCategories.length})
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                startIcon={
                  <Badge badgeContent={activeFiltersCount} color="error">
                    <TuneIcon fontSize="small" />
                  </Badge>
                }
                onClick={() => setMobileFilterOpen(true)}
                sx={{
                  minHeight: 38,
                  borderColor: "#94a3b8",
                  color: "#334155",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  "&:hover": { borderColor: "#004d40", bgcolor: "#f0fdfa" },
                }}
              >
                Filter & Search
              </Button>
              <Button
                fullWidth
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => showAddCategoryModal("ADD")}
                sx={{
                  bgcolor: "#004d40",
                  color: "#ffffff",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  minHeight: 38,
                  boxShadow: "0 2px 8px rgba(0, 77, 64, 0.2)",
                  "&:hover": { bgcolor: "#00382e" },
                }}
              >
                Add Category
              </Button>
            </Stack>

            {hasActiveFilters && (
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 1.25 }}
              >
                {searchQuery.trim() && (
                  <Chip
                    size="small"
                    label={`"${searchQuery}"`}
                    onDelete={() => setSearchQuery("")}
                    sx={{ fontSize: "0.72rem", height: 24 }}
                  />
                )}
                {hierarchyFilter !== "ALL" && (
                  <Chip
                    size="small"
                    label={hierarchyFilter === "ROOT" ? "Root Categories" : "Subcategories"}
                    onDelete={() => setHierarchyFilter("ALL")}
                    sx={{ fontSize: "0.72rem", height: 24 }}
                  />
                )}
                {navbarFilter !== "ALL" && (
                  <Chip
                    size="small"
                    label={navbarFilter === "VISIBLE" ? "Visible in Nav" : "Hidden from Nav"}
                    onDelete={() => setNavbarFilter("ALL")}
                    sx={{ fontSize: "0.72rem", height: 24 }}
                  />
                )}
                {parentFilter !== "ALL" && (
                  <Chip
                    size="small"
                    label={rootCategories.find((c) => c.id === parentFilter)?.name || "Parent"}
                    onDelete={() => setParentFilter("ALL")}
                    sx={{ fontSize: "0.72rem", height: 24 }}
                  />
                )}
                <Button
                  size="small"
                  onClick={handleResetFilters}
                  sx={{ minWidth: 0, p: 0.5, fontSize: "0.72rem", fontWeight: 700, color: "#dc2626" }}
                >
                  Clear
                </Button>
              </Stack>
            )}
          </Box>

          {filteredCategories.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                textAlign: "center",
                borderRadius: 3,
                border: "1px dashed #cbd5e1",
                bgcolor: "#f8fafc",
              }}
            >
              <CategoryOutlinedIcon sx={{ fontSize: 44, color: "#94a3b8", mb: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e293b" }}>
                No categories found
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5, mb: 2 }}>
                Try adjusting your search query or removing active filters.
              </Typography>
              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleResetFilters}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                >
                  Clear all filters
                </Button>
              )}
            </Paper>
          ) : (
            filteredCategories.map((category) => {
              const imgs = category.images || [];
              const hasImg = imgs.length > 0 && imgs[0]?.url;
              const parentName = category.parentCategory?.name;
              const isRoot = !parentName || parentName === "None";
              const isVisible = category.showInNavbar !== false;
              const assignedCount = categoryProductCountMap[category.id] || 0;

              return (
                <Paper
                  key={category.id}
                  elevation={0}
                  onClick={() => setEditCategory(category)}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    borderRadius: 3,
                    border: "1px solid #e2e8f0",
                    bgcolor: "#ffffff",
                    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:active": {
                      transform: "scale(0.99)",
                      bgcolor: "#f8fafc",
                    },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Avatar
                      variant="rounded"
                      src={hasImg ? imgs[0].url : ""}
                      alt={category.name}
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 2.5,
                        border: "1px solid #f1f5f9",
                        bgcolor: "#f8fafc",
                        flexShrink: 0,
                      }}
                    >
                      <FolderOutlinedIcon sx={{ color: "#94a3b8" }} />
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                        <Chip
                          label={isRoot ? "Root" : parentName}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            bgcolor: "#f1f5f9",
                            color: "#475569",
                            maxWidth: "100%",
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#64748b",
                            fontFamily: "ui-monospace, monospace",
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          #{category.id}
                        </Typography>
                      </Stack>

                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 800,
                          color: "#0f172a",
                          lineHeight: 1.3,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {category.name}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 1.5, pt: 1.25, borderTop: "1px solid #f1f5f9" }}
                  >
                    <Chip
                      icon={<Inventory2OutlinedIcon sx={{ fontSize: "14px !important" }} />}
                      label={assignedCount === 1 ? "1 product" : `${assignedCount} products`}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        bgcolor: assignedCount > 0 ? "rgba(0, 77, 64, 0.08)" : "#f1f5f9",
                        color: assignedCount > 0 ? "#004d40" : "#64748b",
                        border: `1px solid ${assignedCount > 0 ? "rgba(0, 77, 64, 0.2)" : "#e2e8f0"}`,
                        "& .MuiChip-icon": {
                          color: assignedCount > 0 ? "#004d40" : "#94a3b8",
                        },
                      }}
                    />

                    <Chip
                      label={isVisible ? "Visible in Nav" : "Hidden"}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleNavbarVisibility(category);
                      }}
                      sx={{
                        height: 22,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        bgcolor: isVisible ? "#ecfdf5" : "#f1f5f9",
                        color: isVisible ? "#047857" : "#64748b",
                        border: `1px solid ${isVisible ? "#a7f3d0" : "#cbd5e1"}`,
                        cursor: "pointer",
                      }}
                    />
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    sx={{ mt: 1.25 }}
                    spacing={0.5}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tooltip title="Edit Category">
                      <IconButton
                        size="small"
                        onClick={() => setEditCategory(category)}
                        sx={{
                          color: "#004d40",
                          bgcolor: "#f0fdf4",
                          "&:hover": { bgcolor: "#dcfce7" },
                        }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Category">
                      <IconButton
                        size="small"
                        onClick={() => setDeleteDialogCategory(category)}
                        sx={{
                          color: "#dc2626",
                          bgcolor: "#fef2f2",
                          "&:hover": { bgcolor: "#fee2e2" },
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>
              );
            })
          )}

          <Drawer
            anchor="bottom"
            open={mobileFilterOpen}
            onClose={() => setMobileFilterOpen(false)}
            PaperProps={{
              sx: {
                borderTopLeftRadius: "24px",
                borderTopRightRadius: "24px",
                height: "min(88vh, 720px)",
                maxHeight: "88vh",
                bgcolor: "#f8fafc",
                border: "1px solid #dbe7e4",
                borderBottom: 0,
                overflow: "hidden",
                boxShadow: "0 -14px 42px rgba(15, 23, 42, 0.18)",
              },
            }}
            ModalProps={{
              keepMounted: true,
              hideBackdrop: true,
            }}
          >
            <Box
              sx={{
                px: 2.25,
                pt: 1.25,
                pb: 1.75,
                borderBottom: "1px solid #dbe7e4",
                background: "linear-gradient(180deg, #ecfdf8 0%, #ffffff 100%)",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: "#cbd5e1",
                  mx: "auto",
                  mb: 1.75,
                }}
              />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
                      Filter & Search
                    </Typography>
                    {activeFiltersCount > 0 && (
                      <Chip
                        size="small"
                        label={activeFiltersCount}
                        aria-label={`${activeFiltersCount} active filters`}
                        sx={{
                          minWidth: 24,
                          bgcolor: "#e6f4f1",
                          color: "#004d40",
                          fontWeight: 800,
                          fontSize: "0.72rem",
                          height: 22,
                        }}
                      />
                    )}
                  </Stack>
                  <Typography variant="caption" sx={{ color: "#64748b", mt: 0.35, display: "block" }}>
                    {filteredCategories.length} of {categories.length} categories
                  </Typography>
                </Box>
                <Tooltip title="Close filters">
                  <IconButton
                    size="small"
                    aria-label="Close filters"
                    onClick={() => setMobileFilterOpen(false)}
                    sx={{ bgcolor: "#f1f5f9", color: "#475569", "&:hover": { bgcolor: "#e2e8f0" } }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                px: 2.25,
                py: 2.25,
                bgcolor: "#f8fafc",
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#334155", mb: 1 }}>
                  Search
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Name, ID, or parent category"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ color: "#64748b" }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery ? (
                      <InputAdornment position="end">
                        <Tooltip title="Clear search">
                          <IconButton size="small" aria-label="Clear search" onClick={() => setSearchQuery("")}>
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: 48,
                      bgcolor: "#ffffff",
                      borderRadius: 2,
                      "& fieldset": { borderColor: "#cbd5e1" },
                      "&.Mui-focused fieldset": { borderColor: "#00695c", borderWidth: 2 },
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                  <AccountTreeOutlinedIcon sx={{ fontSize: 18, color: "#64748b" }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#334155" }}>
                    Hierarchy
                  </Typography>
                </Stack>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={hierarchyFilter}
                  onChange={(_, value) => {
                    if (!value) return;
                    setHierarchyFilter(value);
                    if (value === "ROOT") setParentFilter("ALL");
                  }}
                  aria-label="Category hierarchy"
                  sx={{
                    gap: 0.75,
                    "& .MuiToggleButtonGroup-grouped": {
                      border: "1px solid #cbd5e1 !important",
                      borderRadius: "8px !important",
                      py: 1,
                      bgcolor: "#ffffff",
                      color: "#475569",
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "none",
                    },
                    "& .Mui-selected": {
                      bgcolor: "#e6f4f1 !important",
                      borderColor: "#00796b !important",
                      color: "#004d40 !important",
                    },
                  }}
                >
                  <ToggleButton value="ALL">All</ToggleButton>
                  <ToggleButton value="ROOT">Root</ToggleButton>
                  <ToggleButton value="SUB">Subcategories</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#334155", mb: 1 }}>
                  Navbar visibility
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={navbarFilter}
                  onChange={(_, value) => value && setNavbarFilter(value)}
                  aria-label="Navbar visibility"
                  sx={{
                    gap: 0.75,
                    "& .MuiToggleButtonGroup-grouped": {
                      border: "1px solid #cbd5e1 !important",
                      borderRadius: "8px !important",
                      py: 1,
                      bgcolor: "#ffffff",
                      color: "#475569",
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "none",
                    },
                    "& .Mui-selected": {
                      bgcolor: "#e6f4f1 !important",
                      borderColor: "#00796b !important",
                      color: "#004d40 !important",
                    },
                  }}
                >
                  <ToggleButton value="ALL">All</ToggleButton>
                  <ToggleButton value="VISIBLE">Visible</ToggleButton>
                  <ToggleButton value="HIDDEN">Hidden</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                  <FolderOutlinedIcon sx={{ fontSize: 18, color: "#64748b" }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#334155" }}>
                    Parent category
                  </Typography>
                </Stack>
                <FormControl fullWidth>
                  <Select
                    value={parentFilter}
                    onChange={(e) => {
                      setParentFilter(e.target.value);
                      if (e.target.value !== "ALL") setHierarchyFilter("SUB");
                    }}
                    displayEmpty
                    sx={{
                      height: 48,
                      borderRadius: 2,
                      bgcolor: "#ffffff",
                      color: "#334155",
                      fontWeight: 600,
                      "& fieldset": { borderColor: "#cbd5e1" },
                      "&.Mui-focused fieldset": { borderColor: "#00695c" },
                    }}
                  >
                    <MenuItem value="ALL">All parent categories</MenuItem>
                    {rootCategories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Stack
              direction="row"
              spacing={1.25}
              sx={{
                px: 2.25,
                pt: 1.5,
                pb: "max(20px, env(safe-area-inset-bottom))",
                borderTop: "1px solid #e2e8f0",
                bgcolor: "#ffffff",
                boxShadow: "0 -6px 20px rgba(15, 23, 42, 0.05)",
              }}
            >
              <Button
                variant="outlined"
                color="inherit"
                disabled={!hasActiveFilters}
                onClick={handleResetFilters}
                sx={{
                  minWidth: 96,
                  borderRadius: 2,
                  fontWeight: 700,
                  textTransform: "none",
                  borderColor: "#cbd5e1",
                  color: "#475569",
                }}
              >
                Reset all
              </Button>
              <Button
                variant="contained"
                disabled={filteredCategories.length === 0}
                onClick={() => setMobileFilterOpen(false)}
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  bgcolor: "#004d40",
                  color: "#ffffff",
                  fontWeight: 800,
                  textTransform: "none",
                  py: 1.2,
                  boxShadow: "0 4px 14px rgba(0, 77, 64, 0.22)",
                  "&:hover": { bgcolor: "#00382e" },
                }}
              >
                {filteredCategories.length === 0
                  ? "No categories found"
                  : `Show ${filteredCategories.length} ${filteredCategories.length === 1 ? "category" : "categories"}`}
              </Button>
            </Stack>
          </Drawer>
        </Box>
      )}

      {/* Add / Edit Category Modal */}
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={openCategoryModal}
        onClose={hideAddCategoryModal}
        closeAfterTransition
      >
        <Fade in={openCategoryModal}>
          <Box sx={styles.boxStyle}>
            <AddCategory
              action={editCategoryModal ? "EDIT" : "ADD"}
              hideModal={hideAddCategoryModal}
              handleSetCategory={handleSetCategory}
              onDeleteCategory={handleExecuteDelete}
              products={products}
            />
          </Box>
        </Fade>
      </Modal>

      {/* Delete Confirmation / Cant Delete Dialog */}
      <Dialog
        open={Boolean(deleteDialogCategory)}
        onClose={() => !isDeleting && setDeleteDialogCategory(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px", p: 1 },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: "50%",
              bgcolor: deleteDialogLinkedProducts.length > 0 ? "#fff7ed" : "#fef2f2",
              color: deleteDialogLinkedProducts.length > 0 ? "#ea580c" : "#ef4444",
              display: "flex",
            }}
          >
            <WarningAmberRoundedIcon />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: deleteDialogLinkedProducts.length > 0 ? "#9a3412" : "#1e293b",
            }}
          >
            {deleteDialogLinkedProducts.length > 0 ? "Cant delete category" : "Delete Category"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {deleteDialogLinkedProducts.length > 0 ? (
            <Stack spacing={2}>
              <DialogContentText sx={{ color: "#334155", fontSize: "0.95rem", lineHeight: 1.5 }}>
                Cant delete category <strong>"{deleteDialogCategory?.name}"</strong> because it is currently used by{" "}
                <strong>
                  {deleteDialogLinkedProducts.length} product{deleteDialogLinkedProducts.length > 1 ? "s" : ""}
                </strong>.
              </DialogContentText>
              <Box
                sx={{
                  p: 1.8,
                  bgcolor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    display: "block",
                    mb: 1,
                  }}
                >
                  Linked Products ({deleteDialogLinkedProducts.length}):
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.8,
                    maxHeight: 120,
                    overflowY: "auto",
                  }}
                >
                  {deleteDialogLinkedProducts.map((p, idx) => (
                    <Chip
                      key={p.id || idx}
                      label={p.title || p.name || p.id}
                      size="small"
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        bgcolor: "#ffffff",
                        border: "1px solid #cbd5e1",
                        color: "#334155",
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                To delete this category, please reassign or remove the category from all associated products first.
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <DialogContentText sx={{ color: "#475569", fontSize: "0.95rem" }}>
                Are you sure you want to delete category{" "}
                <strong>"{deleteDialogCategory?.name}"</strong>? This will permanently remove it from your taxonomy.
              </DialogContentText>
              {deleteDialogChildCount > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    bgcolor: "#fffbeb",
                    border: "1px solid #fde68a",
                    borderRadius: "8px",
                    color: "#b45309",
                    fontSize: "0.85rem",
                  }}
                >
                  ⚠️ Warning: <strong>{deleteDialogChildCount}</strong> subcategories are currently linked to this category.
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1.5, justifyContent: "flex-end" }}>
          {deleteDialogLinkedProducts.length > 0 ? (
            <Button
              variant="contained"
              onClick={() => setDeleteDialogCategory(null)}
              sx={{
                borderRadius: "10px",
                fontWeight: 700,
                textTransform: "none",
                bgcolor: "#004d40",
                px: 3,
                py: 0.9,
                fontSize: "0.875rem",
                boxShadow: "0 2px 8px rgba(0, 77, 64, 0.2)",
                transition: "all 0.15s ease",
                "&:hover": {
                  bgcolor: "#00382e",
                },
              }}
            >
              Understood
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={() => setDeleteDialogCategory(null)}
                disabled={isDeleting}
                sx={{
                  borderRadius: "10px",
                  borderColor: "#cbd5e1",
                  color: "#475569",
                  fontWeight: 700,
                  textTransform: "none",
                  px: 2.5,
                  py: 0.9,
                  fontSize: "0.875rem",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: "#94a3b8",
                    bgcolor: "#f8fafc",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteOutlineIcon fontSize="small" />}
                onClick={handleExecuteDelete}
                disabled={isDeleting}
                sx={{
                  borderRadius: "10px",
                  fontWeight: 700,
                  textTransform: "none",
                  bgcolor: "#dc2626",
                  px: 2.8,
                  py: 0.9,
                  fontSize: "0.875rem",
                  boxShadow: "0 2px 8px rgba(220, 38, 38, 0.25)",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    bgcolor: "#b91c1c",
                    boxShadow: "0 4px 12px rgba(220, 38, 38, 0.35)",
                  },
                }}
              >
                {isDeleting ? "Deleting…" : "Delete Category"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbarState}
        autoHideDuration={3500}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={hideSnackbar}
          severity={message.type}
          sx={{ width: "100%", borderRadius: "10px", fontWeight: 600 }}
        >
          {message.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CategoriesListAdmin;
